import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "@/lib/scroll";

const links = [
    { label: "Free preview", href: "#plan" },
    { label: "Why me", href: "#why" },
    { label: "Services", href: "#services" },
    { label: "Pricing", href: "#pricing" },
    { label: "Contact", href: "#contact" },
];

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (href) => {
        setOpen(false);
        // Delay so the mobile menu can collapse before we scroll
        setTimeout(() => scrollToId(href), 60);
    };

    return (
        <motion.header
            data-testid="site-header"
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
            className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
                scrolled
                    ? "bg-[#11141D]/70 backdrop-blur-xl border-b border-white/5"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
                <button
                    data-testid="logo-home"
                    onClick={() => go("#hero")}
                    className="flex items-center gap-3"
                >
                    <Logo className="w-8 h-8" />
                    <span className="font-display text-xl tracking-tight hidden sm:block">
                        The Raw Math
                    </span>
                </button>

                <nav className="hidden md:flex items-center gap-9">
                    {links.map((l) => (
                        <button
                            key={l.href}
                            data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                            onClick={() => go(l.href)}
                            className="overline text-white/70 hover:text-[#C5A059] transition-colors duration-300"
                        >
                            {l.label}
                        </button>
                    ))}
                    <button
                        data-testid="header-cta"
                        onClick={() => go("#pricing")}
                        className="group relative overline text-[#070A13] bg-[#C5A059] rounded-full px-6 py-3 overflow-hidden transition-transform duration-300 hover:-translate-y-0.5"
                    >
                        <span className="relative z-10">Book a call</span>
                        <span className="absolute inset-0 bg-[#C5A059] translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                    </button>
                </nav>

                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen((o) => !o)}
                    className="md:hidden p-2"
                    aria-label="Menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="md:hidden overflow-hidden bg-[#11141D] border-b border-white/10"
                    >
                        <div className="px-6 py-6 flex flex-col gap-5">
                            {links.map((l) => (
                                <button
                                    key={l.href}
                                    onClick={() => go(l.href)}
                                    className="font-display text-3xl text-left tracking-tight"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <button
                                onClick={() => {
                                    setOpen(false);
                                    go("#pricing");
                                }}
                                className="overline text-[#070A13] bg-[#C5A059] rounded-full px-6 py-4 mt-2"
                            >
                                Book a call
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
};
